import React, { createContext, useContext, useState } from 'react';
import { AlertModal, ConfirmModal } from '../components/ui/Modal';

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    code: ''
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'warning',
    isDestructive: false,
    onConfirm: () => {}
  });

  const showAlert = ({ 
    title, 
    message, 
    type = 'info', 
    confirmText = 'OK',
    code = ''
  }) => {
    return new Promise((resolve) => {
      setAlertModal({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        code,
        onClose: () => {
          setAlertModal(prev => ({ ...prev, isOpen: false }));
          resolve();
        }
      });
    });
  };

  const showConfirm = ({ 
    title, 
    message, 
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'warning',
    isDestructive = false 
  }) => {
    return new Promise((resolve) => {
      setConfirmModal({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        type,
        isDestructive,
        onConfirm: () => resolve(true),
        onClose: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  };

  const showSuccess = (message, title = 'Success', { code = '' } = {}) => {
    return showAlert({ title, message, type: 'success', code });
  };

  const showError = (message, title = 'Error') => {
    return showAlert({ title, message, type: 'error' });
  };

  const showWarning = (message, title = 'Warning') => {
    return showAlert({ title, message, type: 'warning' });
  };

  const showInfo = (message, title = 'Information') => {
    return showAlert({ title, message, type: 'info' });
  };

  const showDeleteConfirm = (itemName = 'item') => {
    return showConfirm({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete this ${itemName}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      isDestructive: true
    });
  };

  const value = {
    showAlert,
    showConfirm,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showDeleteConfirm
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={alertModal.onClose}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        confirmText={alertModal.confirmText}
        code={alertModal.code}
      />
      
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.onClose}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        type={confirmModal.type}
        isDestructive={confirmModal.isDestructive}
      />
    </ModalContext.Provider>
  );
}; 